// FORM: https://github.com/modusign/modusign-client/blob/35ac05fa1f9337ff1aeacd765766a3b36653b583/app/src/components/TextField/ConfigurableTextField/index.tsx

import { Box } from '@modusign/design-system';
import { useDateField } from '@modusign/react-signing';
import { cx } from 'emotion';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle, useUpdateEffect } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import {
  DATE_TEXT_LENGTH,
  getDateFieldCorrectedSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/getDateFieldCorrectedSize';
import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import { getTextBaseFieldMinimumSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldMinimumSize';
import {
  AbsolutePoint,
  AbsoluteSize,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import {
  DateFormatPart,
  DateFormatValue,
} from '@app/features/apply-react-signing/components/FieldSetupStep/DateBaseFieldContextMenu/constants';
import DateFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/DateBaseFieldContextMenu/DateFieldContextMenu';
import DateFieldPlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/DateFieldPlaceholder';
import buildDisplayFormatString, {
  parseDateFormatString,
} from '@app/features/apply-react-signing/libs/dateFormatUtil';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import { useTransformTextStyle } from '@app/features/apply-react-signing/libs/hooks/useTransFormTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './ConfigurableDateField.scss';

export function ConfigurableDateFieldRenderer({
  restrictModification,
  readOnly,
  fieldId,
  scale,
  viewport,
  isDataLabelEditable,
}: ReactSigningFieldRendererProps) {
  const [isResizing, setResizing] = useState(false);
  const [showContextMenu, toggleContextMenu] = useToggle(true);
  const [showGuideline, setShowGuideline] = useState(false);
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setAlign,
    setFontSize,
    setFontStyle,
    setRequired,
    setDisplayFormat,
  } = useDateField(fieldId);

  const parsed = useMemo(() => {
    return parseDateFormatString(field?.config.displayFormat);
  }, [field?.config.displayFormat]);

  const [dateFormat, setDateFormat] = useState<DateFormatValue>(parsed.dateFormat);
  const [dateSuffix, setDateSuffix] = useState<DateFormatPart>(parsed.datePart);

  const {
    evaluated: { isDataLabelDuplicated },
    removeField,
  } = useOptimalFieldsContext<'DATE', 'SIGNER'>();

  const fontSize = field?.config.fontStyle.size ?? 60;
  const fontPixelSize: number = PDFFontNormalizer.toPixelFontSize({ fontSize }, scale);

  const absoluteSize = toAbsoluteSize(field?.size ?? { width: 0, height: 0 }, viewport);
  const absolutePoint = toAbsolutePoint(field?.position ?? { x: 0, y: 0 }, viewport);

  const currentFontSize = getTextBaseFieldMinimumSize(fontSize, scale);
  const minimumFontSize = getTextBaseFieldMinimumSize(4, scale);

  const minSize: AbsoluteSize = createAbsoluteSize({
    width: DATE_TEXT_LENGTH * minimumFontSize.width,
    height: Math.max(minimumFontSize.height, currentFontSize.height),
  });

  const { transformTextStyle } = useTransformTextStyle({
    fontFamily: field?.config.fontStyle.type ?? 'NOTO_SANS',
    textAlign: field?.config.textAlign ?? 'LEFT',
    fontSize: field?.config.fontStyle.size ?? 12,
    size: absoluteSize,
    scale,
  });

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [field?.dataLabel, isDataLabelDuplicated]);

  const correctSize = () => {
    const relativeSize = toRelativeSize(
      getDateFieldCorrectedSize({
        size: absoluteSize,
        scale,
        fontSize: field?.config.fontStyle.size ?? 60,
      }),
      viewport
    );
    setSize(relativeSize.width, relativeSize.height);
  };

  // 폰트크기가 변경되었을 때, 최소 크기와 글줄 길이에 맞게 보정합니다.
  useUpdateEffect(() => {
    correctSize();
  }, [fontPixelSize]);

  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const handlePointChangeStart = () => {
    setShowGuideline(true);
    toggleContextMenu(true);
  };

  const handlePointChangeStop = () => {
    setShowGuideline(false);
  };

  const handlePointChange = (point: AbsolutePoint) => {
    const relativePoint = clampFieldPoint(toRelativePoint(point, viewport));
    setPosition(relativePoint.x, relativePoint.y);
    setShowGuideline(true);
  };

  const handleChangeSizeStart = () => {
    setResizing(true);
    setShowGuideline(true);
  };

  const handleChangeSize = (size: AbsoluteSize) => {
    const relativeSize = toRelativeSize(size, viewport);

    setShowGuideline(true);
    setSize(relativeSize.width, relativeSize.height);
  };

  const handleChangeSizeStop = () => {
    setResizing(false);
    setShowGuideline(false);
    correctSize();
  };

  const participantOrder = field.participantReference.index + 1;

  const ConfigurableDateFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableDateField',
    participantOrder
  );

  const handleChangeFontSize = (fontSize: number) => {
    setFontSize(fontSize);
  };

  const handleDatePartChange = (dateSuffix: DateFormatPart) => {
    setDateSuffix(dateSuffix);

    setDisplayFormat(buildDisplayFormatString(dateFormat, dateSuffix));
  };

  const handleDateFormatChange = (dateFormat: DateFormatValue) => {
    setDateFormat(dateFormat);

    setDisplayFormat(buildDisplayFormatString(dateFormat, dateSuffix));
  };

  return (
    <FieldBase
      className={cx({
        ConfigurableDateField: true,
        'ConfigurableDateField--resizing': isResizing,
        [ConfigurableDateFieldClassByOrder]: true,
        'ConfigurableDateField__disable-resize': readOnly || restrictModification,
      })}
      data-testid="date-field"
      dragCancelSelectors={['.ConfigurableDateField__context-menu']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1,
          bottom: 1,
        },
        viewport,
        showGuideLine: showGuideline,
      }}
      maxConstraints={[viewport.width - absolutePoint.x, minSize.height]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      restrictModification={restrictModification}
      size={absoluteSize}
      snapSize={createAbsoluteSize({
        width: 1,
        height: fontPixelSize,
      })}
      onPointChange={handlePointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeStop}
      onSizeChange={handleChangeSize}
      onSizeChangeStart={handleChangeSizeStart}
      onSizeChangeStop={handleChangeSizeStop}
    >
      <Box height="100%" width="100%">
        <OutsideClickHandler
          onOutsideClick={() => {
            if (field.dataLabel && !isDataLabelDuplicated) {
              toggleContextMenu(false);
            }
          }}
        >
          {showContextMenu && !readOnly && !restrictModification && (
            <Box className="ConfigurableDateField__context-menu">
              <DateFieldContextMenu
                dataLabel={field.dataLabel}
                dataLabelErrorMessage={dataLabelErrorMessage}
                dateFormat={dateFormat}
                dateSuffix={dateSuffix}
                fontFamily={field.config.fontStyle.type}
                fontSize={field.config.fontStyle.size}
                isDataLabelEditable={isDataLabelEditable}
                isRequired={field.config.required}
                participantName={getSignerDisplayName(field.participantReference)}
                participantOrder={participantOrder}
                textAlign={field.config.textAlign}
                onDataLabelChange={(dataLabel) => setDataLabel(dataLabel)}
                onDateFormatChange={handleDateFormatChange}
                onDateFormatPartChange={handleDatePartChange}
                onFontFamilyChange={(fontFamily) => setFontStyle(fontFamily)}
                onFontSizeChange={handleChangeFontSize}
                onRequiredToggle={(_, required) => setRequired(required)}
                onTextAlignChange={(align) => setAlign(align)}
              />
            </Box>
          )}
          <Box height="100%" position="absolute" width="100%">
            <DateFieldPlaceholder
              dateFormat={field.config.displayFormat}
              fontPixelSize={fontPixelSize}
              participantOrder={participantOrder}
              star={field.config.required}
              style={transformTextStyle}
            />
            {!readOnly && !restrictModification && (
              <Box className="ConfigurableDateField__x-button">
                <RoundedXButton onClick={() => removeField(field.id)} />
              </Box>
            )}
          </Box>
        </OutsideClickHandler>
      </Box>
    </FieldBase>
  );
}
